import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { connectorsByIdentifier } from "../../shared/connectors";
import {
  connectionTestAlwaysSkips,
  connectionTestWouldSkip,
} from "../../shared/connectors/connectionTestWouldSkip";
import { isConfiguredForConnectionTest } from "../../shared/connectors/isDraft";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useExecutionContent } from "../hooks/useExecutionContent";
import { useVisitorTestReadiness } from "../hooks/useVisitorTestReadiness";
import {
  getConnectorStatus,
  MISSING_WORKFLOW_ERROR,
} from "../lib/connectorStatus";
import { useMakeUrl, routes } from "../lib/urls";
import { Button } from "./Button";
import { ButtonLink } from "./Button/ButtonLink";
import { ButtonPress } from "./Button/ButtonPress";
import { ConnectorHeading, Header } from "./ConnectorModal/Header";
import { CopyToggleButton } from "./CopyToggleButton";
import { EmptyState } from "./EmptyState";
import {
  HttpStatusBadge,
  HttpStatusLine,
  StatusLineRow,
} from "./HttpStatusLine";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { ModalFooter } from "./ModalCard";
import { parseRequestLog, RequestsList } from "./RequestsList";
import { ResultSummary } from "./ResultSummary";
import { Skeleton } from "./Skeleton";
import { StepDetails } from "./StepDetails";
import { Text } from "./Text";

const Body = styled("div")`
  padding: 16px;
  flex: 1;
  min-height: 0;
  border-top: 0.5px solid var(--border-alpha);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RequestsHeading = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 4px 8px;
`;

const TranscriptMessage = styled("div")`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
`;

type Props = { id: string; onClose: () => void };

export function ConnectorTestModal({ id, onClose }: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [app] = useQuery(queries.loadConnectorDetails({ connectedAppId: id }));
  const { visitor, canManage, visitorReady } = useVisitorTestReadiness(app);
  const usesSharedTest =
    app && app.identifier !== "workflow" && (!visitor || visitorReady);
  const connectorError =
    app && getConnectorStatus(app) === "error"
      ? app.identifier === "workflow"
        ? MISSING_WORKFLOW_ERROR
        : app.oauthConnection?.status === "error"
          ? app.oauthConnection.lastError || "Reconnect this connector"
          : null
      : null;
  const showsResult = visitor
    ? app?.lastTestUserId === z.userID
    : usesSharedTest;
  const failed = showsResult && app?.lastTestStatus === "failure";
  const passed = showsResult && app?.lastTestStatus === "success";
  const testing = showsResult && app?.lastTestStatus === "pending";
  const executionId =
    !connectorError && (failed || passed)
      ? (app?.lastTestExecutionId ?? undefined)
      : undefined;
  const errorMessage = testing
    ? null
    : connectorError ||
      (failed ? app?.lastTestMessage || "Connection test failed" : null);
  const httpFailure = errorMessage?.match(
    /^Got status (\d{3})(?:: ([\s\S]*))?$/
  );
  const requests = useExecutionContent(
    executionId
      ? { id: executionId, status: failed ? "error" : "success" }
      : undefined,
    "requests"
  );
  const recordedRequests =
    passed && !requests.loading && requests.content
      ? parseRequestLog(requests.content).filter(
          (line) => line.kind === "request" && !line.data.internal
        )
      : [];
  const singleRequest =
    recordedRequests.length === 1 ? recordedRequests[0] : undefined;
  const successStatusCode =
    singleRequest?.kind === "request" ? singleRequest.data.status : null;

  if (!app) return null;

  const connectionTest = connectorsByIdentifier[app.identifier]?.connectionTest;
  const canTest =
    usesSharedTest &&
    (visitor || isConfiguredForConnectionTest(app)) &&
    !connectionTestWouldSkip(connectionTest, app.configuration);
  const emptyTitle =
    app.identifier === "workflow"
      ? "This connector uses a workflow."
      : app.authorizationMode === "visitor"
        ? canManage
          ? "Test with your own account."
          : "Each caller connects their own account."
        : "This connection hasn’t been tested.";
  const emptyDescription =
    app.identifier === "workflow"
      ? "Open the connector to view its workflow configuration."
      : app.authorizationMode === "visitor"
        ? visitorReady
          ? "Run a test with your account to check this connection."
          : canManage
            ? "Connect your own account through an app, chat, or workflow that uses this connector, then return here to test it."
            : "There’s no shared connection test to display."
        : connectionTestAlwaysSkips(connectionTest)
          ? "Connection tests aren’t supported for this connector."
          : canTest
            ? "Run a test to check this connection."
            : "Configure a connection test in the connector settings.";

  return (
    <Modal
      open
      onClose={onClose}
      style={{
        width: 680,
        height: 760,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header>
        <ConnectorHeading
          app={app}
          title={app.name}
          subtitle="Connection details"
        />
        <ButtonPress
          size="l"
          variant="secondary"
          icon="x"
          aria-label="Close"
          onPress={onClose}
        />
      </Header>
      <Body aria-busy={testing || requests.loading}>
        {testing && (
          <Skeleton
            aria-hidden
            style={{ height: 48, borderRadius: 12, flexShrink: 0 }}
          />
        )}
        {errorMessage && (
          <ResultSummary>
            {httpFailure ? (
              <HttpStatusLine
                statusCode={httpFailure[1]!}
                statusText={httpFailure[2] ?? ""}
              />
            ) : (
              <Text variant="code-regular">{errorMessage}</Text>
            )}
          </ResultSummary>
        )}
        {passed && !connectorError && (
          <ResultSummary>
            {successStatusCode ? (
              <HttpStatusLine
                statusCode={String(successStatusCode)}
                statusText="Connection test passed"
              />
            ) : (
              <StatusLineRow>
                <HttpStatusBadge
                  style={{
                    background: "var(--green-400)",
                    color: "var(--green-1000)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 30,
                    height: 22,
                  }}
                >
                  OK
                </HttpStatusBadge>
                <Text variant="code-regular">Connection test passed</Text>
              </StatusLineRow>
            )}
          </ResultSummary>
        )}
        {testing || requests.loading ? (
          <Skeleton
            role="status"
            aria-label={
              testing ? "Testing connection…" : "Loading request log…"
            }
            style={{ flex: 1, minHeight: 0, borderRadius: 12 }}
          />
        ) : !errorMessage && !passed ? (
          <EmptyState
            iconName="refresh-cw"
            title={emptyTitle}
            description={emptyDescription}
          />
        ) : (
          <StepDetails
            heading={
              <RequestsHeading>
                <Icon name="agent-open" size={12} />
                <Text variant="text-small-md">Requests</Text>
              </RequestsHeading>
            }
            headingRight={
              requests.content && (
                <CopyToggleButton getText={() => requests.content} />
              )
            }
            flushBody
            body={
              requests.content ? (
                <RequestsList
                  content={requests.content}
                  executionId={executionId}
                  defaultExpandSingleRequest
                />
              ) : (
                <TranscriptMessage>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {executionId
                      ? "The transcript is no longer available."
                      : connectorError
                        ? "No request log is available for this error."
                        : "No request log was recorded for this test."}
                  </Text>
                </TranscriptMessage>
              )
            }
          />
        )}
      </Body>
      <ModalFooter
        style={{
          padding: "12px 16px",
          borderTop: "0.5px solid var(--border-alpha)",
          flexShrink: 0,
        }}
      >
        {(connectorError || !canTest) && (
          <ButtonLink
            size="l"
            variant="subtle"
            to={makeUrl(routes.modal.connector, { id })}
          >
            Edit connector
          </ButtonLink>
        )}
        {canTest && (
          <Button
            size="l"
            variant="subtle"
            icon="refresh-cw"
            iconSpinning={testing}
            disabled={testing}
            aria-busy={testing}
            style={
              testing ? { background: "var(--alpha8)", opacity: 1 } : undefined
            }
            onClick={() => {
              if (!testing)
                z.mutate(
                  sharedMutators.testConnectedApp({ connectedAppId: id })
                );
            }}
          >
            Test connection
          </Button>
        )}
        <ButtonPress size="l" onPress={onClose}>
          Done
        </ButtonPress>
      </ModalFooter>
    </Modal>
  );
}
